import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TERMINAL_RUN_STATUSES,
  type ChangedFile,
  type LogLine,
  type RunDto,
  type RunSummaryDto,
  type StageDto,
} from '@aiai/shared';
import {
  cancelRun,
  fetchIssue,
  fetchLoop,
  fetchProject,
  fetchRun,
  fetchRunAttempts,
  fetchRunLogs,
  iterateRun,
  mergeRunPullRequest,
  refreshRunPullRequest,
  updateRunPullRequestBranch,
} from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { projectNav } from '../lib/projectNav';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useCapabilities } from '../hooks/useCapabilities';
import { useIsBasicWorkspace } from '../hooks/useIsBasicWorkspace';
import { useRunSocket } from '../hooks/useRunSocket';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LiveDot } from '../components/Badge';
import { PageHeader } from '../components/PageHeader';
import { SegmentedTabs } from '../components/Tabs';
import { StatusBadge } from '../components/StatusBadge';
import { PrStateBadge } from '../components/PrStateBadge';
import { ChangesPanel } from '../components/ChangesPanel';
import { AgentTimeline } from '../components/AgentTimeline';
import { type TimelineLine } from '../components/timeline';
import { Spinner } from '../components/Spinner';
import { ErrorState, LoadingState } from '../components/States';
import { formatDuration, formatRelative } from '../lib/format';
import { fmtElapsed } from './runDetail/runFormat';
import { RunAlerts } from './runDetail/RunAlerts';
import { RunSidebar } from './runDetail/RunSidebar';

/**
 * Panes of the single work area. Live runs get logs; terminal runs gain the
 * post-run Changes pane instead.
 */
type WorkTab = 'logs' | 'changes';

export function RunDetailPage() {
  const { runId = '' } = useParams();
  const navigate = useNavigate();
  const { wp } = useWorkspacePath();
  // Members may view a run but not cancel it, merge/update its PR, or iterate
  // (owner-only). Refreshing PR state is a harmless read, so it stays.
  const { can } = useCapabilities();
  // Basic (shared-rail) workspaces run under the platform's dedicated GitHub org
  // the customer can't access, and their PRs auto-merge — so PR/GitHub controls
  // (badge link, refresh, merge/update) are dead ends and are hidden for them.
  const isBasic = useIsBasicWorkspace();
  const canStopRun = can('run:stop');
  const canMergePr = can('pr:merge') && !isBasic;
  const canTriggerRun = can('run:trigger');
  const queryClient = useQueryClient();

  // Local, socket-mutable copies seeded from the REST snapshot.
  const [run, setRun] = useState<RunDto | null>(null);
  const [logs, setLogs] = useState<TimelineLine[]>([]);
  // The one segmented work area (logs + post-run panes).
  const [tab, setTab] = useState<WorkTab>('logs');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [mergePrDialogOpen, setMergePrDialogOpen] = useState(false);
  const seenLogSeq = useRef<Set<number>>(new Set());
  const replaySeq = useRef(0);

  const isTerminal = run ? TERMINAL_RUN_STATUSES.includes(run.status) : false;

  // Auto-switch to the Changes tab the first time a completed run reports
  // changed files, so the diff is immediately visible without a manual tab click.
  const changesAutoSelected = useRef(false);
  useEffect(() => {
    if (!isTerminal || changesAutoSelected.current || !run?.changedFiles.length) return;
    changesAutoSelected.current = true;
    setTab('changes');
  }, [isTerminal, run?.changedFiles.length]);

  const runQuery = useQuery({
    queryKey: qk.run(runId),
    queryFn: () => fetchRun(runId),
    enabled: !!runId,
    // Poll as a fallback while the run is active (in case the socket drops).
    refetchInterval: (query) => {
      const data = query.state.data as RunDto | undefined;
      if (data && TERMINAL_RUN_STATUSES.includes(data.status)) return false;
      return 8000;
    },
  });

  const logsQuery = useQuery({
    queryKey: qk.runLogs(runId),
    queryFn: () => fetchRunLogs(runId),
    enabled: !!runId,
  });

  const attemptsQuery = useQuery({
    queryKey: qk.runAttempts(runId),
    queryFn: () => fetchRunAttempts(runId),
    enabled: !!runId && isTerminal,
    staleTime: Infinity,
  });

  // Fetch issue + project so the breadcrumb shows real names (the RunDto only
  // carries their ids), matching the Issue detail page's breadcrumb chain.
  // Prefer the short code so the fetch (and its query key) are code-first,
  // matching every other issue link; fall back to the UUID pre-backfill.
  const issueId = run?.issueId ?? '';
  const issueRef = run?.issueCode ?? issueId;
  const projectId = run?.projectId ?? '';
  const projectRef = run?.projectCode ?? projectId;
  const issueQuery = useQuery({
    queryKey: qk.issue(issueRef),
    queryFn: () => fetchIssue(issueRef),
    enabled: !!issueRef,
  });
  const projectQuery = useQuery({
    queryKey: qk.project(projectRef),
    queryFn: () => fetchProject(projectRef),
    enabled: !!projectRef,
  });
  const loopId = issueQuery.data?.loopId ?? null;
  const loopQuery = useQuery({
    queryKey: qk.loop(loopId ?? ''),
    queryFn: () => fetchLoop(loopId!),
    enabled: !!loopId,
    staleTime: Infinity,
  });

  useDocumentTitle(
    run
      ? issueQuery.data?.title
        ? `${issueQuery.data.title} — run ${run.id.slice(0, 8)}`
        : `Run ${run.id.slice(0, 8)}`
      : undefined,
  );

  // Seed run snapshot whenever a fresh REST copy arrives (keeps stages fresh
  // when polling, while socket events still apply on top between fetches).
  useEffect(() => {
    if (runQuery.data) setRun(runQuery.data);
  }, [runQuery.data]);

  // Seed the replay buffer once.
  useEffect(() => {
    if (!logsQuery.data) return;
    const replay: TimelineLine[] = logsQuery.data.map((line) => ({
      ...line,
      seq: --replaySeq.current, // negative, monotonically decreasing
    }));
    setLogs(replay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsQuery.data]);

  // ── Live socket ─────────────────────────────────────────────────────────--
  const socketStatus = useRunSocket(isTerminal ? undefined : runId, {
    // On reconnect, re-pull the REST snapshot + replay buffer to backfill any
    // events missed while the socket was down (seq de-dup makes this safe).
    onReconnect: () => {
      queryClient.invalidateQueries({ queryKey: qk.run(runId) });
      queryClient.invalidateQueries({ queryKey: qk.runLogs(runId) });
    },
    onLog: (line: LogLine, seq: number) => {
      if (seenLogSeq.current.has(seq)) return;
      seenLogSeq.current.add(seq);
      setLogs((prev) => [...prev, { ...line, seq }]);
    },
    onStage: (stage: StageDto) => {
      setRun((prev) =>
        prev
          ? {
              ...prev,
              stages: prev.stages.some((s) => s.id === stage.id)
                ? prev.stages.map((s) => (s.id === stage.id ? stage : s))
                : [...prev.stages, stage],
            }
          : prev,
      );
    },
    onAgentEvent: (payload: Record<string, unknown>) => {
      if (payload?.kind === 'files' && Array.isArray(payload.files)) {
        setRun((prev) =>
          prev ? { ...prev, changedFiles: payload.files as ChangedFile[] } : prev,
        );
      }
    },
    onStatus: (summary: RunSummaryDto) => {
      setRun((prev) => (prev ? { ...prev, ...summary } : prev));
      if (TERMINAL_RUN_STATUSES.includes(summary.status)) {
        // Refresh REST snapshot + related lists once the run settles.
        queryClient.invalidateQueries({ queryKey: qk.run(runId) });
        queryClient.invalidateQueries({ queryKey: qk.runLogs(runId) });
        if (summary.issueId) {
          // Key off the same code-first ref the issue query above uses, or the
          // invalidation misses it (RunSummaryDto carries no code).
          queryClient.invalidateQueries({ queryKey: qk.issue(issueRef) });
          queryClient.invalidateQueries({ queryKey: qk.issueRuns(issueRef) });
        }
      }
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelRun(runId),
    onSuccess: (updated) => {
      setRun(updated);
      queryClient.invalidateQueries({ queryKey: qk.run(runId) });
      setCancelDialogOpen(false);
    },
  });

  const refreshPrMutation = useMutation({
    mutationFn: () => refreshRunPullRequest(runId),
    onSuccess: (updated) => {
      setRun(updated);
      queryClient.invalidateQueries({ queryKey: qk.run(runId) });
    },
  });

  const mergeMutation = useMutation({
    mutationFn: () => mergeRunPullRequest(runId),
    onSuccess: (updated) => {
      setRun(updated);
      queryClient.invalidateQueries({ queryKey: qk.run(runId) });
    },
  });

  const updateBranchMutation = useMutation({
    mutationFn: () => updateRunPullRequestBranch(runId),
    onSuccess: (updated) => {
      setRun(updated);
      queryClient.invalidateQueries({ queryKey: qk.run(runId) });
    },
  });

  const iterateMutation = useMutation({
    mutationFn: (feedback: string) => iterateRun(runId, { feedback }),
    onSuccess: (newRun) => navigate(wp(`/runs/${newRun.id}`)),
  });

  const live = !isTerminal && socketStatus === 'connected';

  // Ticking clock for a live elapsed timer while the run is active.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (isTerminal) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isTerminal]);

  // Most recent meaningful log line = "what the agent is doing right now".
  const lastActivity = useMemo(() => {
    for (let i = logs.length - 1; i >= 0; i--) {
      const text = logs[i].text?.trim();
      if (text) return text;
    }
    return null;
  }, [logs]);

  if (runQuery.isLoading && !run) return <LoadingState label="Loading run…" />;
  if (runQuery.isError && !run) {
    return (
      <ErrorState
        message={errorMessage(runQuery.error, 'Failed to load run')}
        onRetry={() => runQuery.refetch()}
      />
    );
  }
  if (!run) return <LoadingState label="Loading run…" />;

  const tabOptions: Array<{ value: WorkTab; label: ReactNode }> = [
    { value: 'logs', label: 'Logs' },
  ];
  if (isTerminal && run.changedFiles.length > 0)
    tabOptions.push({ value: 'changes', label: 'Changes' });

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={(() => {
          const nav = projectNav(projectQuery.data);
          const issueRefForCrumb = issueQuery.data?.code ?? run.issueId;
          return [
            { label: nav.rootLabel, to: wp(nav.rootTo) },
            { label: nav.name, to: wp(nav.selfTo) },
            // Chat (issue-less) runs have no issue crumb to show.
            ...(issueRefForCrumb
              ? [
                  {
                    label: issueQuery.data?.code
                      ? `${issueQuery.data.code} ${issueQuery.data.title}`
                      : (issueQuery.data?.title ?? 'Issue'),
                    to: wp(`/issues/${issueRefForCrumb}`),
                  },
                ]
              : []),
            { label: `run ${run.id.slice(0, 8)}` },
          ];
        })()}
      />

      <PageHeader
        title={issueQuery.data?.title ?? `run ${run.id.slice(0, 8)}`}
        titleClassName={issueQuery.data?.title ? undefined : 'font-mono'}
        badges={
          <>
            <StatusBadge status={run.status} />
            {!isBasic && (
              <PrStateBadge
                url={run.pullRequestUrl}
                state={run.pullRequestState}
                size="md"
                showMerged
              />
            )}
            {!isTerminal && socketStatus !== 'connected' && (
              <span className="text-xs text-warning">
                {socketStatus === 'connecting'
                  ? 'connecting…'
                  : socketStatus === 'reconnecting'
                    ? 'reconnecting — live view will resume…'
                    : 'disconnected — this view may be stale'}
              </span>
            )}
          </>
        }
        subtitle={
          <>
            <span className="font-mono">run {run.id.slice(0, 8)}</span>
            {' · '}
            {projectQuery.data?.name ?? 'Project'} · {run.pipeline.join(' → ')}{' '}
            ·{' '}
            {run.startedAt
              ? `started ${formatRelative(run.startedAt)}`
              : `queued ${formatRelative(run.createdAt)}`}{' '}
            ·{' '}
            {formatDuration(run.startedAt, run.finishedAt)}
            {run.parentRunId && (
              <>
                {' · iteration of '}
                <Link
                  to={wp(`/runs/${run.parentRunId}`)}
                  className="font-mono text-signal hover:underline"
                >
                  run {run.parentRunId.slice(0, 8)}
                </Link>
              </>
            )}
          </>
        }
        actions={
          <>
            {run.pullRequestUrl && run.pullRequestState === 'open' && !isBasic && (
              <button
                type="button"
                title="Re-check this PR's state on GitHub"
                className="btn-secondary btn-sm"
                disabled={refreshPrMutation.isPending}
                onClick={() => refreshPrMutation.mutate()}
              >
                {refreshPrMutation.isPending && (
                  <Spinner className="h-3.5 w-3.5" />
                )}
                Refresh PR
              </button>
            )}
            {!isTerminal && canStopRun && (
              <button
                type="button"
                className="btn-danger"
                disabled={cancelMutation.isPending}
                onClick={() => setCancelDialogOpen(true)}
              >
                {cancelMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
                Cancel run
              </button>
            )}
          </>
        }
      />

      <RunAlerts
        run={run}
        isBasic={isBasic}
        canMergePr={canMergePr}
        onMergeClick={() => setMergePrDialogOpen(true)}
        mergePending={mergeMutation.isPending}
        onUpdateBranch={() => updateBranchMutation.mutate()}
        updateBranchPending={updateBranchMutation.isPending}
        updateBranchError={
          updateBranchMutation.isError
            ? errorMessage(updateBranchMutation.error, 'Could not update branch')
            : null
        }
        iterateError={
          iterateMutation.isError
            ? errorMessage(iterateMutation.error, 'Could not start iteration')
            : null
        }
      />

      {!isTerminal && (
        <div className="card flex items-center gap-3 px-4 py-3">
          <LiveDot className="shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-2xs font-medium uppercase tracking-wide text-ink-faint">
              {run.startedAt ? (
                <>Currently · {fmtElapsed(now - new Date(run.startedAt).getTime())}</>
              ) : (
                <>Queued · {fmtElapsed(now - new Date(run.createdAt).getTime())}</>
              )}
            </div>
            <div className="truncate font-mono text-xs text-ink">
              {lastActivity ?? 'Starting…'}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-[1fr_280px] lg:grid-cols-[1fr_320px]">
        {/* Main: the one segmented work area */}
        <div className="min-w-0 space-y-3">
          <SegmentedTabs
            value={tab}
            onChange={setTab}
            options={tabOptions}
            ariaLabel="Run work area"
          />

          {tab === 'changes' && run.changedFiles.length > 0 ? (
            <ChangesPanel files={run.changedFiles} diff={run.diff} defaultOpen />
          ) : logsQuery.isLoading && logs.length === 0 ? (
            <div className="card">
              <LoadingState label="Loading logs…" />
            </div>
          ) : (
            <AgentTimeline
              lines={logs}
              live={live}
              height="h-[60vh] sm:h-[620px]"
              feedback={run.feedback}
              onIterate={
                isTerminal && run.branch && run.commitSha && canTriggerRun
                  ? (f) => iterateMutation.mutate(f)
                  : undefined
              }
              iterating={iterateMutation.isPending}
            />
          )}
        </div>

        {/* Right rail: pipeline + metadata */}
        <RunSidebar
          run={run}
          isTerminal={isTerminal}
          attempts={attemptsQuery.data}
          attemptsError={attemptsQuery.isError}
          loop={loopQuery.data}
          loopId={loopId}
          projectRef={projectRef}
        />
      </div>

      <ConfirmDialog
        open={cancelDialogOpen}
        title="Cancel run?"
        confirmLabel="Cancel run"
        message="This will stop the running agent immediately. Any uncommitted work inside the container will be lost."
        busy={cancelMutation.isPending}
        error={
          cancelMutation.isError
            ? errorMessage(cancelMutation.error, 'Could not cancel run')
            : null
        }
        onConfirm={() => cancelMutation.mutate()}
        onClose={() => setCancelDialogOpen(false)}
      />

      <ConfirmDialog
        open={mergePrDialogOpen}
        title="Merge pull request?"
        confirmLabel="Merge pull request"
        busy={mergeMutation.isPending}
        error={
          mergeMutation.isError
            ? errorMessage(mergeMutation.error, 'Could not merge the pull request')
            : null
        }
        onConfirm={() => mergeMutation.mutate()}
        onClose={() => { setMergePrDialogOpen(false); mergeMutation.reset(); }}
        message={
          <>
            Merge the pull request for{' '}
            <span className="font-medium text-ink">{issueQuery.data?.title ?? 'this issue'}</span>? This cannot be undone.
          </>
        }
      />
    </div>
  );
}
